import React from 'react';
import { useThemeMode, systemMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type DashboardStatsTableProps = {
  metrics: any;
  loading?: boolean;
};

export const DashboardStatsTable: React.FC<DashboardStatsTableProps> = ({ metrics, loading }) => {
  const { mode } = useThemeMode();
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

  // Get current month and last month names
  const now = new Date();
  const currentMonthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthName = lastMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Calculate same day last week and last month
  const lastWeekDate = new Date(now);
  lastWeekDate.setDate(now.getDate() - 7);
  const lastWeekSameDay = lastWeekDate.toLocaleDateString('default', { weekday: 'short', day: 'numeric' });

  const lastMonthSameDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const lastMonthSameDay = lastMonthSameDate.toLocaleDateString('default', { month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div
        className="card"
        style={{
          backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
          border: isDarkMode ? '1px solid #2d2d3f' : '1px solid #e4e6ef',
        }}
      >
        <div className="card-header border-0 pt-5">
          <h3 className="card-title">
            <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>
              Dashboard Stats
            </span>
          </h3>
        </div>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <span className="spinner-border text-primary"></span>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const rows = [
    {
      label: 'Total Jobs',
      icon: 'bi-list-check',
      color: 'primary',
      values: [
        metrics.totalJobsToday,
        metrics.totalJobsYesterday,
        metrics.totalJobsLastWeekSameDay,
        metrics.totalJobsLastMonthSameDay,
        metrics.totalJobsThisWeek,
        metrics.totalJobsLastWeek,
        metrics.totalJobsThisMonth,
        metrics.totalJobsLastMonth,
      ],
    },
    {
      label: 'Active Jobs',
      icon: 'bi-graph-up',
      color: 'warning',
      values: [
        metrics.activeJobsToday,
        metrics.activeJobsYesterday,
        metrics.activeJobsLastWeekSameDay,
        metrics.activeJobsLastMonthSameDay,
        metrics.activeJobsThisWeek,
        metrics.activeJobsLastWeek,
        metrics.activeJobsThisMonth,
        metrics.activeJobsLastMonth,
      ],
    },
    {
      label: 'Avg Jobs/Day',
      icon: 'bi-calculator',
      color: 'info',
      values: [
        metrics.avgJobsToday,
        metrics.avgJobsYesterday,
        metrics.avgJobsLastWeekSameDay,
        metrics.avgJobsLastMonthSameDay,
        metrics.avgJobsThisWeek,
        metrics.avgJobsLastWeek,
        metrics.avgJobsThisMonth,
        metrics.avgJobsLastMonth,
      ],
    },
    {
      label: 'Completed Jobs',
      icon: 'bi-check-circle-fill',
      color: 'success',
      values: [
        metrics.completedJobsToday,
        metrics.completedJobsYesterday,
        metrics.completedJobsLastWeekSameDay,
        metrics.completedJobsLastMonthSameDay,
        metrics.completedJobsThisWeek,
        metrics.completedJobsLastWeek,
        metrics.completedJobsThisMonth,
        metrics.completedJobsLastMonth,
      ],
    },
    {
      label: 'Water Wash',
      icon: 'bi-droplet-fill',
      color: 'primary',
      values: [
        metrics.waterWashToday,
        metrics.waterWashYesterday,
        metrics.waterWashLastWeekSameDay,
        metrics.waterWashLastMonthSameDay,
        metrics.waterWashThisWeek,
        metrics.waterWashLastWeek,
        metrics.waterWashThisMonth,
        metrics.waterWashLastMonth,
      ],
    },
  ];

  const columns = [
    { label: 'Today', subtext: null },
    { label: 'Yesterday', subtext: null },
    { label: 'Last Week', subtext: `(${lastWeekSameDay})` },
    { label: 'Last Month', subtext: `(${lastMonthSameDay})` },
    { label: 'This Week', subtext: null },
    { label: 'Last Week', subtext: null },
    { label: 'This Month', subtext: currentMonthName },
    { label: 'Last Month', subtext: lastMonthName },
  ];

  return (
    <div
      className="card card-flush h-xl-100"
      style={{
        backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
        border: isDarkMode ? '1px solid #2d2d3f' : '1px solid #e4e6ef',
        boxShadow: '0 0 20px 0 rgba(76, 87, 125, 0.02)',
      }}
    >
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className={`card-label fw-bolder fs-3 mb-1 ${isDarkMode ? 'text-white' : 'text-dark'}`}>
            Dashboard Stats
          </span>
          <span className="text-muted mt-1 fw-bold fs-7">Key performance indicators over time</span>
        </h3>
        <div className="card-toolbar">
          <span className={`badge badge-light-success fw-bold fs-7`}>Live Updates</span>
        </div>
      </div>
      <div className="card-body pt-2 pb-5">
        <div className="table-responsive">
          <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
              <tr className="fw-bolder fs-7 text-muted text-uppercase ls-1">
                <th className="min-w-150px ps-4 text-start">ITEM</th>
                {columns.map((col, idx) => (
                  <th 
                    key={idx} 
                    className="text-center" 
                    style={{ 
                      whiteSpace: 'pre-line', 
                      minWidth: '100px',
                      borderLeft: (idx === 4 || idx === 6) ? `1px dashed ${isDarkMode ? '#323248' : '#e4e6ef'}` : 'none'
                    }}
                  >
                    {col.label}
                    {col.subtext && (
                      <span className="d-block fs-9 fw-bold text-gray-400 mt-1 text-capitalize ls-0">
                        {col.subtext}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="ps-4">
                    <div className="d-flex align-items-center">
                        <div className={`symbol symbol-40px me-3`}>
                            <div className={`symbol-label bg-light-${row.color} text-${row.color}`}>
                                <i className={`bi ${row.icon} fs-2 text-${row.color}`}></i>
                            </div>
                        </div>
                        <div>
                            <span className={`fw-bold fs-6 ${isDarkMode ? 'text-white' : 'text-dark'} d-block`}>
                                {row.label}
                            </span>
                        </div>
                    </div>
                  </td>
                  {row.values.map((value, colIdx) => {
                    const isZero = value === 0 || value === '0.0';
                    return (
                      <td 
                        key={colIdx} 
                        className="text-center"
                        style={{
                            borderLeft: (colIdx === 4 || colIdx === 6) ? `1px dashed ${isDarkMode ? '#323248' : '#e4e6ef'}` : 'none'
                        }}
                      >
                        <span className={`fw-bold fs-6 ${isZero ? 'text-gray-400' : (isDarkMode ? 'text-white' : 'text-dark')}`} style={{ opacity: isZero ? 0.5 : 1 }}>
                          {typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
